<script setup lang="ts">
import BlogCard from '@/components/blog/BlogCard.vue'
import { blogs } from '@/data/blogs'
import { getBlogFolderByCategory, getLatestBlog } from '@/data/blogs'
import { useHead } from '@unhead/vue'
import { easeOut, motion } from 'motion-v'

const route = useRoute()

const { t } = useI18n()

const blogSection = ref<HTMLElement | null>(null)

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${import.meta.env.VITE_MONOLITH_URL}/blog`,
    },
  ],
})

useSeoMeta({
  title: t('blog.meta.title'),
  description: t('blog.meta.description'),
  ogTitle: t('blog.meta.title'),
  ogDescription: t('blog.meta.description'),
})

const categories = [
  'All Articles',
  'Restaurant Marketing',
  'Restaurant SEO',
  'Restaurant Website Design',
  'Restaurant Customer Retention',
  'Restaurant AI Marketing',
]

const latestBlog = getLatestBlog(blogs)

const folder = getBlogFolderByCategory(latestBlog.category)

const activeCategory = ref(
  (route.query.category as string) || 'All Articles',
)

const filteredBlogs = computed(() => {
  if (activeCategory.value === 'All Articles') {
    return blogs
  }
  return blogs.filter(blog => blog.category === activeCategory.value)
})

const blogsToShow = ref(6)
const displayedBlogs = computed(() => filteredBlogs.value.slice(0, blogsToShow.value))
const loadMore = () => {
  blogsToShow.value += 6
}

// Motion variants
const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const blogVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const buttonVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: easeOut } },
}

const scrollToBlogs = async () => {
  await nextTick()
  if (blogSection.value) {
    blogSection.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

watch(
  () => route.query.category,
  async (newCategory) => {
    activeCategory.value = (newCategory as string) || 'All Articles'
    await scrollToBlogs()
  },
)

onMounted(async () => {
  await nextTick()
  if (route.query.category) {
    console.log('Mounted with category:', route.query.category)
    scrollToBlogs()
  }
})
</script>

<template>
  <div class="isolate">
    <LayoutNavbar />

    <div class="mb-0 bg-gradient-to-b from-[#f8f8f0] to-[#f8f8f0] pt-10 md:shadow-2xl md:shadow-black/10">
      <!-- Headline -->
      <section class="3xl:px-0 mx-auto mb-8 px-4 lg:max-w-screen-xl">
        <motion.h1
          class="block  text-center text-3xl font-medium  !leading-tight text-gray-700 lg:text-5xl"
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
        >
          Every Second Counts!
        </motion.h1>
        <motion.p
          class="pt-6 text-center text-gray-600"
          :initial="{ opacity: 0, y: 20 }"
          :while-in-view="{ opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.4 } }"
        >
          Quick marketing basics for owners who can’t afford to lose another customer.
        </motion.p>
      </section>

      <!-- Featured Blog -->
      <section class="3xl:px-0 mx-auto mb-8 px-4 lg:max-w-screen-xl">
        <motion.div
          class="flex w-full flex-col gap-x-8 gap-y-4 rounded-[2.5rem] border border-neutral-200 bg-white px-6 py-4 md:flex-row lg:px-14  lg:py-12"
          :initial="{ opacity: 0, y: 50 }"
          :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
        >
          <img
            :src="latestBlog.image"
            :alt="latestBlog.alt"
            class="w-full shrink rounded-[1rem] object-cover drop-shadow-lg md:w-1/2"
          >
          <div class="flex w-full flex-col items-start justify-between md:w-1/2 ">
            <p class="text-base text-gray-500">
              {{ latestBlog.category }} • {{ latestBlog.date }}
            </p>
            <h2 class="leading-1 text-2xl font-bold text-gray-700">
              {{ latestBlog.title }}
            </h2>
            <p class="whitespace-pre-line text-gray-600 md:line-clamp-4 lg:line-clamp-6">
              {{ latestBlog.description }}
            </p>
            <UiButton
              class="group mt-4 w-full"
              as-child
              color="neutral"
            >
              <RouterLink
                class="w-max"
                :to="`/blog/${folder}/${latestBlog.slug}`"
                color="accent"
              >
                Read The Article
                <UiIcon
                  name="arrow-forward-rounded"
                  class="visible block transition-transform duration-200 group-hover:translate-x-1"
                />
              </RouterLink>
            </UiButton>
          </div>
        </motion.div>
      </section>

      <!-- Categories -->
      <div class="mb-8 w-full bg-white  pb-8 pt-8 md:rounded-[5rem] ">
        <section
          ref="blogSection"
          class="mx-auto mb-8 px-2 md:px-4 lg:max-w-screen-xl lg:px-10 "
        >
          <motion.div
            class="mb-10 flex flex-wrap justify-center gap-3 px-4 xl:justify-start xl:px-0"
            :initial="{ opacity: 0, y: 20 }"
            :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5 } }"
          >
            <button
              v-for="category in categories"
              :key="category"
              class="w-full rounded-xl px-5 py-3 text-sm font-medium transition md:w-max"
              :class="[
                activeCategory === category
                  ? 'bg-orange-500 text-white'
                  : 'bg-[#31323E14] text-gray-700 hover:bg-[#31323E25]'
              ]"
              @click="activeCategory = category"
            >
              {{ category }}
            </button>
          </motion.div>

          <!-- Blog Cards -->
          <div class="grid w-full grid-cols-1 gap-x-4 gap-y-12 md:grid-cols-3 xl:gap-x-8">
            <motion.div
              v-for="(blog, index) in displayedBlogs"
              :key="blog.slug"
              :custom="index"
              :variants="blogVariants"
              initial="initial"
              while-in-view="animate"
            >
              <BlogCard v-bind="blog" />
            </motion.div>
          </div>

          <!-- Load More -->
          <motion.div
            class="mt-8 flex justify-center"
            :variants="buttonVariants"
            initial="initial"
            while-in-view="animate"
          >
            <button
              v-if="blogsToShow < filteredBlogs.length"
              class="rounded-xl bg-orange-500 px-6 py-3 font-medium text-white transition hover:bg-orange-600"
              @click="loadMore"
            >
              Load More Articles
            </button>
          </motion.div>
        </section>

        <!-- CTA Section -->
        <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
          <div class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20">
            <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
              <motion.img
                :initial="{ opacity: 0, y: 50 }"
                :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              />
            </div>

            <div class="isolate grid place-items-center gap-5">
              <motion.h3
                :variants="headlineVariants"
                initial="initial"
                while-in-view="animate"
                class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
              >
                Ready to take your restaurant marketing to the next level?
              </motion.h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  Book a Free Demo

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
